import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from "react";

import {
  PrivyClientConfig,
  PrivyProvider,
  useMfaEnrollment,
  // useSetWalletRecovery,
  usePrivy,
  useWallets,
  // useFundWallet,
} from "@privy-io/react-auth";
import {
  SmartWalletsProvider,
  useSmartWallets,
} from "@privy-io/react-auth/smart-wallets";

import {
  Account,
  Chain,
  createWalletClient,
  custom,
  Hash,
  http,
  LocalAccount,
  maxUint256,
  parseAbi,
  Transport,
  WalletClient,
} from "viem";

import {
  arbitrum,
  avalanche,
  base,
  baseSepolia,
  mainnet,
  optimism,
  polygon,
  sepolia,
} from "viem/chains";
import { createPimlicoClient } from "permissionless/clients/pimlico";
import {
  entryPoint06Address,
  toCoinbaseSmartAccount,
  UserOperation,
} from "viem/account-abstraction";
import {
  CASH_TOKEN_ADDRESS_BY_CHAIN,
  GET_PIMLICO_URL,
  PRIVY_APP_ID,
  PRIVY_APP_ID_DEV,
  darkThemeBase,
  lightThemeBase,
} from "@src/config";
import { createSmartAccountClient, erc20BalanceOverride } from "permissionless";
import { SwapperStyles } from "@src/models";
import { logger } from "@src/utils";

const CHAIN_BY_ID = {
  "11155111": sepolia,
  "84532": baseSepolia,
  "1": mainnet,
  "8453": base,
  "43114": avalanche,
  "42161": arbitrum,
  "10": optimism,
  "137": polygon,
};

export interface UserOperationGasPrice {
  maxFeePerGas: bigint;
  maxPriorityFeePerGas: bigint;
}

interface PrivySmartWalletContextType {
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  email?: string;
  smartWalletAddress?: string;
  signerWalletAddress?: string;
  executeTransaction: (
    calls: Array<{
      to: `0x${string}`;
      value?: bigint;
      data?: `0x${string}`;
    }>,
    chainId: string,
    gasCostInUSDC?: string,
  ) => Promise<string | undefined>;
  showMfaEnrollmentModalIfNotEnrolled: () => boolean;
  getGasFee: (
    calls: Array<{
      to: `0x${string}`;
      value?: bigint;
      data?: `0x${string}`;
    }>,
    chainId: string,
  ) => Promise<string | undefined>;
}

// Create the context
const PrivySmartWalletContext = createContext<
  PrivySmartWalletContextType | undefined
>(undefined);

// Hook to use the context
export const usePrivySmartWallet = () => {
  const context = useContext(PrivySmartWalletContext);
  if (context === undefined) {
    throw new Error(
      "usePrivySmartWallet must be used within a PrivySmartWalletProvider",
    );
  }
  return context;
};

// Inner provider that uses Privy hooks
const PrivyInnerProvider = ({ children }: { children: ReactNode }) => {
  const {
    user,
    authenticated,
    login: privyLogin,
    logout: privyLogout,
  } = usePrivy();
  const [smartWalletAddress, setSmartWalletAddress] = useState<
    string | undefined
  >(undefined);
  const { showMfaEnrollmentModal } = useMfaEnrollment();
  const { wallets } = useWallets();
  const embeddedWallet = wallets.find(
    (wallet) => wallet.walletClientType === "privy",
  );
  const { client } = useSmartWallets();
  const getClientsForChain = useCallback(
    async (chainId: string, userOperationGasPrice?: UserOperationGasPrice) => {
      if (!embeddedWallet) return;
      const embeddedWalletProvider = await embeddedWallet.getEthereumProvider();
      if (!embeddedWalletProvider) return;

      const pimlicoClient = createPimlicoClient({
        chain: CHAIN_BY_ID[chainId],
        transport: http(GET_PIMLICO_URL(chainId)),
        entryPoint: {
          address: entryPoint06Address,
          version: "0.6",
        },
      });

      // // Create a viem WalletClient from the embedded wallet's EIP1193 provider
      // // This will be used as the signer for the user's smart account
      const privyClient = createWalletClient({
        account: embeddedWallet.address as `0x${string}`,
        chain: CHAIN_BY_ID[chainId],
        transport: custom(embeddedWalletProvider),
      });
      await privyClient.switchChain({ id: CHAIN_BY_ID[chainId].id });

      const walletClientToLocalAccount = <
        TChain extends Chain | undefined = Chain | undefined,
      >(
        walletClient: WalletClient<Transport, TChain, Account>,
      ): LocalAccount => {
        return {
          address: walletClient.account.address,
          type: "local",
          source: "privy",
          publicKey: walletClient.account.address,
          signMessage: async () => {
            throw new Error("signMessage Not Implemented");
          },
          signTypedData: async () => {
            throw new Error("signTypedData Not Implemented");
          },
          signTransaction: async () => {
            throw new Error("signTransaction Not Implemented");
          },
          sign: async ({ hash }) => {
            // Based on privy react-auth source code this is the correct way to sign a message with the privy wallet client
            // Only sign method is required to be implemented
            return await walletClient.request<{
              ReturnType: Hash;
            }>({
              method: "secp256k1_sign",
              params: [hash],
            });
          },
        };
      };

      const signer = walletClientToLocalAccount(privyClient);
      const smartAccount = await toCoinbaseSmartAccount({
        client: privyClient,
        owners: [signer],
      });

      const smartAccountClient = createSmartAccountClient({
        account: smartAccount,
        chain: CHAIN_BY_ID[chainId],
        bundlerTransport: http(GET_PIMLICO_URL(chainId)),
        paymaster: pimlicoClient,
        userOperation: {
          estimateFeesPerGas: async () => {
            if (userOperationGasPrice) {
              return userOperationGasPrice;
            }
            logger.log("Estimate Fee per gas callback");
            return (await pimlicoClient.getUserOperationGasPrice()).fast;
          },
        },
      });
      return {
        smartAccountClient,
        pimlicoClient,
      };
    },
    [embeddedWallet],
  );

  useEffect(() => {
    if (client) {
      setSmartWalletAddress(client.account.address);
      window.dispatchEvent(
        new CustomEvent("swapper:login", {
          detail: {
            success: true,
            smartWalletAddress: client.account.address,
          },
        }),
      );
    } else {
      setSmartWalletAddress(undefined);
    }
  }, [client]);

  const authenticatedRef = useRef(authenticated);
  useEffect(() => {
    authenticatedRef.current = authenticated;
  }, [authenticated, user]);
  const loginPromise = useCallback(async (): Promise<void> => {
    // If already logged in, resolve instantly
    if (authenticatedRef.current) return;

    // Call login
    privyLogin();
    if (!authenticatedRef.current) {
      await new Promise((resolve) => {
        const check = () => {
          if (authenticatedRef.current) {
            resolve(true);
          } else setTimeout(check, 100);
        };
        check();
      });
    }
  }, [privyLogin]);

  // Login function
  const login = useCallback(async () => {
    try {
      logger.log("Initiating Privy login...");
      await loginPromise();
      logger.log("Privy login successful");
    } catch (error) {
      console.error("Error logging in with Privy:", error);
    }
  }, [loginPromise]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      logger.log("Logging out from Privy...");
      await privyLogout();
      logger.log("Privy logout successful");

      // Dispatch custom event for logout success
      window.dispatchEvent(
        new CustomEvent("swapper:logout", {
          detail: {
            success: true,
          },
        }),
      );
    } catch (error) {
      console.error("Error logging out from Privy:", error);
    }
  }, [privyLogout]);

  // Log for debugging
  useEffect(() => {
    logger.log("Authentication state:", {
      // client: client,
      authenticated,
      userId: user?.id,
      smartWalletAddress,
    });
  }, [authenticated, user, smartWalletAddress]);

  const getGasFee = useCallback(
    async (
      calls: Array<{
        to: `0x${string}`;
        value?: bigint;
        data?: `0x${string}`;
      }>,
      chainId: string,
      userOperationGasPrice?: UserOperationGasPrice,
    ) => {
      const clients = await getClientsForChain(chainId, userOperationGasPrice);

      if (!clients) return;
      const usdc: `0x${string}` = CASH_TOKEN_ADDRESS_BY_CHAIN[chainId];

      const quotes = await clients.pimlicoClient.getTokenQuotes({
        tokens: [usdc],
        chain: CHAIN_BY_ID[chainId],
      });

      if (!quotes || !quotes[0]) {
        throw new Error("No quotes found");
      }
      const { postOpGas, exchangeRate, balanceSlot, paymaster } = quotes[0];

      const hasBalanceSlot = balanceSlot !== undefined;

      const balanceStateOverride = hasBalanceSlot
        ? erc20BalanceOverride({
            token: usdc,
            owner: clients.smartAccountClient.account.address,
            slot: balanceSlot,
          })[0]
        : undefined;
      logger.log("query prepare user operation with:", {
        calls,
      });
      const userOperation: UserOperation<"0.6"> =
        await clients.smartAccountClient.prepareUserOperation({
          calls: [
            {
              abi: parseAbi(["function approve(address,uint256)"]),
              functionName: "approve",
              args: [paymaster, maxUint256],
              to: usdc,
            },
            ...calls,
          ],
          paymasterContext: {
            token: usdc,
          },
          stateOverride: balanceStateOverride
            ? [
                {
                  address: usdc,
                  stateDiff: [...(balanceStateOverride.stateDiff ?? [])],
                },
              ]
            : undefined,
        });
      logger.log("getGasFee user operation", userOperation);

      const userOperationMaxGas =
        userOperation.preVerificationGas +
        userOperation.callGasLimit +
        userOperation.verificationGasLimit;

      const userOperationMaxCost =
        userOperationMaxGas * userOperation.maxFeePerGas;

      // using formula here https://github.com/pimlicolabs/singleton-paymaster/blob/main/src/base/BaseSingletonPaymaster.sol#L334-L341
      const maxCostInToken =
        ((userOperationMaxCost + postOpGas * userOperation.maxFeePerGas) *
          exchangeRate) /
        BigInt(1e18);
      return maxCostInToken.toString();
    },
    [getClientsForChain],
  );

  /**
   * Executes a transaction through the smart contract wallet
   * @param calls Array of contract calls to execute in a single transaction
   * @returns Transaction hash if successful, null if failed or wallet not ready
   */
  const executeTransaction = useCallback(
    async (
      calls: Array<{
        to: `0x${string}`;
        value?: bigint;
        data?: `0x${string}`;
      }>,
      chainId: string,
      gasCostInUSDC?: string,
      userOperationGasPrice?: UserOperationGasPrice,
    ): Promise<Hash | undefined> => {
      try {
        const clients = await getClientsForChain(
          chainId,
          userOperationGasPrice,
        );

        if (!clients) return;

        const usdc: `0x${string}` = CASH_TOKEN_ADDRESS_BY_CHAIN[chainId];
        const quotes = await clients.pimlicoClient.getTokenQuotes({
          tokens: [usdc],
          chain: CHAIN_BY_ID[chainId],
        });

        if (!quotes || !quotes[0]) {
          throw new Error("No quotes found");
        }

        const { postOpGas, exchangeRate, paymaster } = quotes[0];

        let maxCostInToken = gasCostInUSDC ? BigInt(gasCostInUSDC) : 0n;

        if (!gasCostInUSDC) {
          const userOperation: UserOperation<"0.6"> =
            await clients.smartAccountClient.prepareUserOperation({
              calls: [
                {
                  abi: parseAbi(["function approve(address,uint256)"]),
                  functionName: "approve",
                  args: [paymaster, maxUint256],
                  to: usdc,
                },
                ...calls,
              ],
              paymasterContext: {
                token: usdc,
              },
            });

          const userOperationMaxGas =
            userOperation.preVerificationGas +
            userOperation.callGasLimit +
            userOperation.verificationGasLimit;

          const userOperationMaxCost =
            userOperationMaxGas * userOperation.maxFeePerGas;

          // using formula here https://github.com/pimlicolabs/singleton-paymaster/blob/main/src/base/BaseSingletonPaymaster.sol#L334-L341
          maxCostInToken =
            ((userOperationMaxCost + postOpGas * userOperation.maxFeePerGas) *
              exchangeRate) /
            BigInt(1e18);
        }

        const callsWithApproval = [
          {
            abi: parseAbi(["function approve(address,uint256)"]),
            functionName: "approve",
            args: [paymaster, maxCostInToken],
            to: usdc,
          },
          ...calls,
        ];
        const tx = await clients.smartAccountClient.sendTransaction({
          calls: callsWithApproval,
          paymasterContext: {
            token: usdc,
          },
          //TODO:
          // gas,
          // gasPrice,
          // maxFeePerGas,
        });
        logger.log("tx", tx);
        return tx;
      } catch (error) {
        console.error("Failed to execute transaction:", error);
        throw error;
      }
    },
    [getClientsForChain],
  );

  // Expose event listener methods for the parent application
  window.swapper = {
    ...window.swapper,
    privyLogout: logout,
    privyLogin: login,
    onLogin: (callback: (event: CustomEvent) => void) => {
      window.addEventListener("swapper:login", callback as EventListener);
    },
    onLogout: (callback: (event: CustomEvent) => void) => {
      window.addEventListener("swapper:logout", callback as EventListener);
    },
    removeLoginListener: (callback: (event: CustomEvent) => void) => {
      window.removeEventListener("swapper:login", callback as EventListener);
    },
    removeLogoutListener: (callback: (event: CustomEvent) => void) => {
      window.removeEventListener("swapper:logout", callback as EventListener);
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).showMfaEnrollmentModal = showMfaEnrollmentModal;

  // shows modal if user has no MFA methods connected to account and return true. Returns false otherwise
  const showMfaEnrollmentModalIfNotEnrolled = useCallback((): boolean => {
    if (!user) {
      throw new Error("Privy User is undefined");
    }
    if (user?.mfaMethods?.length > 0) {
      return false;
    }
    showMfaEnrollmentModal();
    return true;
  }, [showMfaEnrollmentModal, user]);

  const state = useMemo<PrivySmartWalletContextType>(
    () => ({
      isAuthenticated: authenticated,
      login,
      logout,
      email: user?.email?.address,
      smartWalletAddress,
      signerWalletAddress: embeddedWallet?.address,
      executeTransaction,
      showMfaEnrollmentModalIfNotEnrolled,
      getGasFee,
    }),
    [
      authenticated,
      login,
      logout,
      user?.email?.address,
      smartWalletAddress,
      embeddedWallet?.address,
      executeTransaction,
      showMfaEnrollmentModalIfNotEnrolled,
      getGasFee,
    ],
  );

  return (
    <PrivySmartWalletContext.Provider value={state}>
      {children}
    </PrivySmartWalletContext.Provider>
  );
};

// Main provider that includes Privy provider
export const PrivySmartWalletProvider = ({
  children,
  styles,
  development = false,
}: {
  children: ReactNode;
  styles?: SwapperStyles;
  development?: boolean;
}) => {
  // Determine theme mode from styles, default to light
  const isDarkMode = styles?.themeMode === "dark";

  // Get primary color from styles or CSS variables
  const primaryColor: `#${string}` | undefined =
    (styles?.brandTheme?.primaryColor as `#${string}`) ||
    (isDarkMode ? darkThemeBase.primaryColor : lightThemeBase.primaryColor);

  const darkModeConfig: PrivyClientConfig["appearance"] = {
    theme: "dark",
    accentColor: primaryColor, // Use our dark theme primary
    logo: "https://swapper.finance/swapper_logo_light.svg",
  };

  const lightModeConfig: PrivyClientConfig["appearance"] = {
    theme: "light",
    accentColor: primaryColor, // Use our light theme primary
    logo: "https://swapper.finance/swapper_logo_dark.svg",
  };

  // Select appropriate Privy app ID based on development flag
  const privyAppId = development ? PRIVY_APP_ID_DEV : PRIVY_APP_ID;

  return (
    <PrivyProvider
      appId={privyAppId}
      config={{
        loginMethods: ["email"],
        appearance: isDarkMode ? darkModeConfig : lightModeConfig,
        embeddedWallets: {
          createOnLogin: "all-users",
          showWalletUIs: false,
        },
        mfa: {
          noPromptOnMfaRequired: false,
        },
      }}
    >
      <SmartWalletsProvider>
        <PrivyInnerProvider>{children}</PrivyInnerProvider>
      </SmartWalletsProvider>
    </PrivyProvider>
  );
};

// Extend Window interface to include our custom properties
declare global {
  interface Window {
    swapper: {
      privyLogout: () => Promise<void>;
      privyLogin: () => Promise<void>;
      onLogin: (callback: (event: CustomEvent) => void) => void;
      onLogout: (callback: (event: CustomEvent) => void) => void;
      removeLoginListener: (callback: (event: CustomEvent) => void) => void;
      removeLogoutListener: (callback: (event: CustomEvent) => void) => void;
    };
  }
}
